import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Sparkles, X } from "lucide-react";

/**
 * One-time, opt-in nudge surfaced to senior users (Director/VP/Principal/Staff
 * detected in their base resume). Suggests switching to ProMo for a more
 * professional look. Once a user accepts OR dismisses, `promo_suggested` is
 * persisted and we never show it again.
 *
 * Renders ONLY when the backend says should_show=true AND user is on Playful.
 */
export default function ProMoNudge() {
  const { user } = useAuth();
  const { mode, setMode } = useTheme();
  const [signal, setSignal] = useState(null);
  const [visible, setVisible] = useState(false);

  // Fetch suggestion once when auth resolves
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/account/preferences");
        const s = data?.promo_suggestion;
        if (cancelled) return;
        if (s?.should_show && data?.ui_mode === "playful") {
          setSignal(s.signal || "your role");
          // Slight delay so the nudge feels intentional, not jarring on load
          setTimeout(() => !cancelled && setVisible(true), 1200);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Stop showing if user manually switched mode somewhere
  useEffect(() => {
    if (mode === "promo") setVisible(false);
  }, [mode]);

  const markSeen = async () => {
    try {
      await api.patch("/account/preferences", { promo_suggested: true });
    } catch {
      /* ignore */
    }
  };

  const handleAccept = async () => {
    setVisible(false);
    await Promise.all([
      setMode("promo"),
      markSeen(),
    ]);
  };

  const handleDismiss = async () => {
    setVisible(false);
    await markSeen();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 max-w-sm"
      data-testid="promo-nudge"
      role="dialog"
      aria-live="polite"
    >
      <div
        className="rm-card-static p-5"
        style={{
          background: "#0F1B2D",
          color: "white",
          borderColor: "#FF6A00",
          borderWidth: 3,
          boxShadow: "8px 8px 0 0 #FF6A00",
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "#FF6A00", color: "white", border: "2px solid #FF6A00" }}
          >
            <Sparkles size={20} strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div
              className="rm-label mb-1"
              style={{ color: "#FFC145" }}
              data-testid="promo-nudge-signal"
            >
              Spotted: {signal}
            </div>
            <div className="font-display text-lg uppercase leading-tight">
              Try the seasoned look?
            </div>
            <p className="text-sm mt-2" style={{ color: "#CBD5E1" }}>
              ProMo swaps the mascot and playful copy for a restrained, navy palette —
              built for senior professionals.
            </p>
            <div className="flex gap-2 mt-4 flex-wrap">
              <button
                onClick={handleAccept}
                className="rm-btn rm-btn-primary text-xs px-3 py-2"
                data-testid="promo-nudge-accept"
              >
                Try ProMo
              </button>
              <button
                onClick={handleDismiss}
                className="rm-btn text-xs px-3 py-2"
                style={{
                  background: "transparent",
                  color: "white",
                  borderColor: "white",
                  boxShadow: "2px 2px 0 0 rgba(255,255,255,0.4)",
                }}
                data-testid="promo-nudge-dismiss"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="opacity-70 hover:opacity-100 flex-shrink-0"
            data-testid="promo-nudge-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
